import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FaShoppingCart } from "react-icons/fa";
import useStore from "../../store/useStore";
import usePincodeCheck from "../../utils/usePincodeCheck";
import { getActivePincode } from "../../utils/location";
import { apiFetch } from "../../utils/apiFetch";
import WishlistButton from "./WishlistButton";
import { StarRating } from "./ProductDetailView/BadgeAndRating";
import ImageGallery from "./ProductDetailView/ImageGallery";
import ProductDetailsAccordion from "./ProductDetailView/ProductDetailsAccordion";
import PincodeChecker from "./ProductDetailView/PincodeChecker";
import NotifyMeButton from "./ProductDetailView/NotifyMeButton";

const ProductDetailView = ({
  product,
  addToCart,
  cart,
  cartLoading,
  isAuthenticated,
  openAuthModal,
}) => {
  const navigate = useNavigate();
  const { incrementItem, decrementItem, fetchProductDetail } = useStore();
  const defaultPincode = useStore((state) => getActivePincode(state) || "");
  const [selectedVariant, setSelectedVariant] = useState(() => {
    // Lazy initializer: runs ONCE before the first render.
    // If this product is already in the cart, start with the variant that is
    // in the cart so the stepper is visible immediately — no useEffect needed.
    const pid = product.product_id ?? product.id;
    const inCartItem = cart.find(
      (item) => String(item.product_id ?? item.id) === String(pid)
    );
    if (inCartItem) {
      const cartVid =
        inCartItem.product_variant_id != null ? String(inCartItem.product_variant_id)
        : inCartItem.variant_id        != null ? String(inCartItem.variant_id)
        : null;
      if (cartVid) {
        const matched = (product.variants || []).find(
          (v) => String(v.product_variant_id ?? v.variant_id ?? v.id ?? '') === cartVid
        );
        if (matched) return matched;
      }
    }
    return product.variants?.[0] || null;
  });
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [pincode, setPincode] = useState(defaultPincode);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [productCoupons, setProductCoupons] = useState([]);

  const productId = product.product_id ?? product.id;

  useEffect(() => {
    let active = true;
    if (!productId) return undefined;
    apiFetch(`api/v1/customer/coupons/applicable-to-product/${productId}`)
      .then((response) => { if (active) setProductCoupons(Array.isArray(response.data) ? response.data : []); })
      .catch(() => { if (active) setProductCoupons([]); });
    return () => { active = false; };
  }, [productId]);

  useEffect(() => {
    if (defaultPincode) {
      setPincode(defaultPincode);
    }
  }, [defaultPincode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePincodeChange = ({ pincode: newPin, isComplete }) => {
    setPincode(newPin);
    if (isComplete && newPin.length === 6) {
      const slugOrKey = product.slug || product.product_slug || productId;
      fetchProductDetail(slugOrKey, newPin);
    }
  };

  const { status: pincodeStatus, message: pincodeMessage, productAvailability, stockLevel, availableStock } = usePincodeCheck(
    pincode,
    productId,
    // Pass selected variant id for variant-level stock checks
    selectedVariant?.product_variant_id ?? selectedVariant?.variant_id ?? null,
  );

  // Out of stock at this pincode (delivery available but product not)
  const isPincodeOOS =
    pincodeStatus === "serviceable" &&
    productAvailability?.product_available === false;

  // Global OOS from API fields (is_in_stock, stock_status, is_out_of_stock, available_stock)
  const isProductGloballyOOS =
    product.is_out_of_stock === true ||
    product.is_in_stock === false ||
    product.stock_status === "OUT_OF_STOCK" ||
    product.inStock === false ||
    (product.available_stock !== undefined && product.available_stock !== null && Number(product.available_stock) === 0);

  // Variant-level OOS: check selected variant's own stock fields
  const isVariantOOS =
    selectedVariant?.is_out_of_stock === true ||
    selectedVariant?.is_in_stock === false ||
    selectedVariant?.stock_status === "OUT_OF_STOCK" ||
    selectedVariant?.inStock === false ||
    (selectedVariant?.available_stock !== undefined && selectedVariant?.available_stock !== null && Number(selectedVariant?.available_stock) === 0);

  // Combined: OOS if any of the above
  const isOutOfStock = isPincodeOOS || isProductGloballyOOS || isVariantOOS;

  // ── Granular stock level (from location-aware API) ─────────────────────────
  // Priority: pincode-resolved > variant-level > product-level
  const resolvedStockStatus = stockLevel ?? productAvailability?.stock_status ??
    selectedVariant?.stock_status ?? product.stock_status ?? null;
  const resolvedAvailableStock = availableStock ?? productAvailability?.available_stock ??
    selectedVariant?.available_stock ?? product.available_stock ?? null;
  const isLowStock     = !isOutOfStock && resolvedStockStatus === 'LOW_STOCK';     // 1-5 units
  const isLimitedStock = !isOutOfStock && resolvedStockStatus === 'LIMITED_STOCK'; // 6-10 units
  const isInStock      = !isOutOfStock && resolvedStockStatus === 'IN_STOCK';      // >10 units

  const images = product.images || [];

  // Track whether the initial cart→variant sync has already been done.
  // After it fires once, cart changes should NOT override the user's manual selection.
  const cartSyncDoneRef = React.useRef(false);

  // ONE-SHOT: on mount, if the product is already in the cart, start from that variant.
  useEffect(() => {
    if (cartSyncDoneRef.current) return;
    if (!product?.variants?.length) return;

    const inCartItem = cart.find(
      (item) => String(item.product_id ?? item.id) === String(productId)
    );
    const cartVid =
      inCartItem?.product_variant_id != null ? String(inCartItem.product_variant_id)
      : inCartItem?.variant_id        != null ? String(inCartItem.variant_id)
      : null;

    if (cartVid) {
      const matched = product.variants.find(
        (v) => String(v.product_variant_id ?? v.variant_id ?? v.id ?? '') === cartVid
      );
      if (matched) {
        setSelectedVariant(matched);
        cartSyncDoneRef.current = true;
        return;
      }
    }

    cartSyncDoneRef.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When product.variants loads/changes asynchronously (e.g. cache miss → API arrives),
  // keep the selected variant valid — but do NOT react to cart changes.
  useEffect(() => {
    if (!product?.variants?.length) return;

    setSelectedVariant((prev) => {
      if (!prev) return product.variants[0];
      const prevVid = String(prev.product_variant_id ?? prev.variant_id ?? prev.id ?? '');
      const stillExists = product.variants.find(
        (v) => String(v.product_variant_id ?? v.variant_id ?? v.id ?? '') === prevVid
      );
      return stillExists || product.variants[0];
    });
  }, [product?.variants, productId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Robust variant-aware cart item lookup
  const cartItem = React.useMemo(() => {
    if (!productId || !cart?.length) return null;

    const selVid =
      selectedVariant?.product_variant_id != null ? String(selectedVariant.product_variant_id)
      : selectedVariant?.variant_id        != null ? String(selectedVariant.variant_id)
      : selectedVariant?.id               != null ? String(selectedVariant.id)
      : null;

    // 1. Exact variant match
    const exactMatch = cart.find((item) => {
      if (String(item.product_id ?? item.id) !== String(productId)) return false;
      const itemVid =
        item.product_variant_id != null ? String(item.product_variant_id)
        : item.variant_id        != null ? String(item.variant_id)
        : null;
      if (!itemVid && !selVid) return true;
      if (!itemVid || !selVid) return false;
      return itemVid === selVid;
    });
    if (exactMatch) return exactMatch;

    // 2. Single-variant or no-variant products:
    // If the product has at most 1 variant, any cart item matching product_id IS this item
    const variantsCount = product.variants?.length ?? 0;
    if (variantsCount <= 1) {
      const anyMatch = cart.find(
        (item) => String(item.product_id ?? item.id) === String(productId)
      );
      if (anyMatch) return anyMatch;
    }

    // 3. Fallback when cart item was added without variant ID and selectedVariant is default first variant
    const firstVid =
      product.variants?.[0]?.product_variant_id != null ? String(product.variants[0].product_variant_id)
      : product.variants?.[0]?.variant_id        != null ? String(product.variants[0].variant_id)
      : product.variants?.[0]?.id               != null ? String(product.variants[0].id)
      : null;

    if (selVid && firstVid && selVid === firstVid) {
      const noVidMatch = cart.find((item) => {
        if (String(item.product_id ?? item.id) !== String(productId)) return false;
        const itemVid =
          item.product_variant_id != null ? String(item.product_variant_id)
          : item.variant_id        != null ? String(item.variant_id)
          : null;
        return !itemVid;
      });
      if (noVidMatch) return noVidMatch;
    }

    return null;
  }, [cart, productId, selectedVariant, product.variants]);

  const isInCart = !!cartItem;
  const displayQuantity = isInCart ? cartItem.quantity : quantity;

  // Build a map of variant key → cart item for ALL variants of this product
  // so we can show in-cart badges on each variant pill button.
  const variantCartMap = React.useMemo(() => {
    const map = {};
    if (!productId || !cart.length) return map;
    const variantsList = product.variants || [];
    for (const variant of variantsList) {
      const vKey = String(
        variant.product_variant_id ?? variant.variant_id ?? variant.id ?? ''
      );
      const vId =
        variant.product_variant_id != null ? String(variant.product_variant_id)
        : variant.variant_id        != null ? String(variant.variant_id)
        : variant.id               != null ? String(variant.id)
        : null;
      const found = cart.find((item) => {
        if (String(item.product_id ?? item.id) !== String(productId)) return false;
        const itemVid =
          item.product_variant_id != null ? String(item.product_variant_id)
          : item.variant_id        != null ? String(item.variant_id)
          : null;
        if (!itemVid && !vId) return true;
        if (!itemVid || !vId) {
          if (variantsList.length <= 1) return true;
          return false;
        }
        return itemVid === vId;
      });
      if (found) map[vKey] = found;
    }
    return map;
  }, [cart, productId, product.variants]);


  const handleIncrement = async () => {
    if (isInCart) {
      if (cartLoading) return;
      // Pass the actual cart item so the store uses its exact product_variant_id
      await incrementItem(cartItem);
    } else {
      setQuantity((prev) => prev + 1);
    }
  };

  const handleDecrement = async () => {
    if (isInCart) {
      if (cartLoading) return;
      // Pass the actual cart item so the store uses its exact product_variant_id
      await decrementItem(cartItem);
    } else {
      setQuantity((prev) => Math.max(1, prev - 1));
    }
  };

  const currentPrice = selectedVariant
    ? parseFloat(selectedVariant.discount_price)
    : product.discount_price
      ? parseFloat(product.discount_price)
      : 321.0;
  const currentOriginalPrice = selectedVariant
    ? parseFloat(selectedVariant.original_price)
    : product.original_price
      ? parseFloat(product.original_price)
      : 399.0;
  const discount =
    currentOriginalPrice && currentPrice && currentOriginalPrice > currentPrice
      ? Math.round(
          ((currentOriginalPrice - currentPrice) / currentOriginalPrice) * 100,
        )
      : 20;
  const savings =
    currentOriginalPrice && currentPrice
      ? (currentOriginalPrice - currentPrice).toFixed(2)
      : "78.00";

  const handleAddToCart = async () => {
    if (!selectedVariant) {
      toast.error("Please select a size/weight", { id: "select-size-toast" });
      return;
    }
    // Guests are allowed to add to cart (saved to localStorage guest cart).
    // No login required here — the checkout step will prompt login when needed.
    setAddingToCart(true);
    try {
      await addToCart({ ...product, selectedVariant }, 1);
      toast.success("Added to cart!", { id: `add-cart-${product.product_id ?? product.id}` });
    } catch (err) {
      toast.error(err.message || "Failed to add to cart");
    } finally {
      setAddingToCart(false);
    }
  };

  // Buy Now: add to cart (if not already present) then redirect to /cart.
  // Guests are allowed — cart is public. Login is prompted at checkout.
  const handleBuyNow = async () => {
    if (!selectedVariant) {
      toast.error("Please select a size/weight", {
        id: "select-size-toast",
      });
      return;
    }

    if (isInCart) {
      navigate("/cart");
      return;
    }

    await addToCart({ ...product, selectedVariant }, quantity);
    toast.success("Added to cart!", {
      id: `buy-now-cart-${product.product_id ?? product.id}`,
    });
    navigate("/cart");
  };

  const handleToggleAccordion = () => {
    if (!accordionOpen) {
      setAccordionOpen(true);
      setTimeout(() => {
        const elem = document.getElementById("product-details-accordion");
        if (elem) {
          elem.scrollIntoView({ behavior: "smooth" });
        }
      }, 50);
    } else {
      setAccordionOpen(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start">
        {/* Left Side: Product Image Gallery */}
        <ImageGallery
          images={images}
          productName={product.name}
          discount={discount}
        />

        {/* Right Side: Product Details */}
        <div className="space-y-4">
          {/* Header Row: Title & Subtitle + Wishlist Button */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl lg:text-4xl">
                {product.name || "Groundnut oil"}
              </h1>
              <p className="mt-0.5 text-sm font-medium leading-normal text-gray-700 sm:text-base">
                {[product.product_name_hindi, product.product_name_marathi]
                  .filter(Boolean)
                  .join(" / ") || "मूँगफली का तेल / शेंगदाणा तेल"}
              </p>
            </div>
            <WishlistButton
              productId={product.product_id ?? product.id}
              size="lg"
              className="shrink-0 shadow-sm"
            />
          </div>

          {/* Ratings */}
          <StarRating rating={product.rating || 4.5} count={product.reviews_count || 128} />

          {productCoupons.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" aria-label="Available product offers">
              {productCoupons.map((coupon) => (
                <span key={coupon.coupon_id ?? coupon.coupon_code} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-[#f2faf5] px-3 py-1.5 text-xs font-bold text-[#046a38]">
                  <span aria-hidden="true">✦</span>
                  {coupon.coupon_name || coupon.coupon_code}
                  {coupon.coupon_code && <span className="rounded bg-white px-1.5 py-0.5 tracking-wide">{coupon.coupon_code}</span>}
                </span>
              ))}
            </div>
          )}

          {/* Pricing & Availability Mint Box */}
          <div className="rounded-2xl bg-[#f2faf5] p-5 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              {/* Left Column: Original Price, Savings, Pincode */}
              <div className="flex flex-col justify-between">
                <div>
                  <div className="text-base font-bold text-gray-800 line-through sm:text-lg">
                    ₹ {currentOriginalPrice ? currentOriginalPrice.toFixed(2) : "399.00"}
                  </div>
                  <div className="mt-0.5 text-xs font-semibold text-[#00a859] sm:text-sm">
                    You save ₹ {savings} in this order.
                  </div>
                </div>

                <PincodeChecker
                  initialPincode={pincode}
                  productId={productId}
                  onPincodeChange={handlePincodeChange}
                />
              </div>

              {/* Right Column: Discount Badge, Selling Price, Urgency Indicator */}
              <div className="flex flex-col items-start sm:items-end">
                {discount > 0 && (
                  <span className="rounded-full bg-[#ea580c] px-3 py-0.5 text-xs font-bold uppercase tracking-wide text-white">
                    {discount}% OFF
                  </span>
                )}
                <div className="mt-1 text-3xl font-extrabold text-[#00a859] sm:text-4xl">
                  ₹{currentPrice ? currentPrice.toFixed(2) : "321.00"}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  {pincodeStatus === "not_serviceable" || pincodeStatus === "error" ? (
                    <>
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-amber-500 text-amber-500">
                        <svg className="h-3.5 w-3.5 stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
                        </svg>
                      </div>
                      <div className="leading-tight text-left">
                        <div className="text-xs font-bold text-amber-600 sm:text-sm">{pincodeMessage || "Delivery is not available in your area."}</div>
                      </div>
                    </>
                  ) : isOutOfStock ? (
                    <>
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-amber-500 text-amber-500">
                        <svg className="h-3.5 w-3.5 stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
                        </svg>
                      </div>
                      <div className="leading-tight text-left">
                        <div className="text-xs font-bold text-amber-600 sm:text-sm">Out of Stock</div>
                        <div className="text-xs font-medium text-gray-500 sm:text-sm">Currently unavailable at your location</div>
                      </div>
                    </>
                  ) : isLowStock && resolvedAvailableStock !== null ? (
                    <>
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-red-500 text-red-500">
                        <svg className="h-3.5 w-3.5 stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
                        </svg>
                      </div>
                      <div className="leading-tight text-left">
                        <div className="text-xs font-bold text-red-600 sm:text-sm">Only {resolvedAvailableStock} left!</div>
                        <div className="text-xs font-medium text-gray-500 sm:text-sm">Order soon before it sells out</div>
                      </div>
                    </>
                  ) : isLimitedStock ? (
                    <>
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-[#ea580c] text-[#ea580c]">
                        <svg className="h-3.5 w-3.5 stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="leading-tight text-left">
                        <div className="text-xs font-bold text-[#00a859] sm:text-sm">Hurry!</div>
                        <div className="text-xs font-bold text-[#ea580c] sm:text-sm">Limited Stock Available</div>
                      </div>
                    </>
                  ) : isInStock ? (
                    <>
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-[#00a859] text-[#00a859]">
                        <svg className="h-3.5 w-3.5 stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="leading-tight text-left">
                        <div className="text-xs font-bold text-[#00a859] sm:text-sm">In Stock</div>
                        <div className="text-xs font-medium text-gray-500 sm:text-sm">Available for fast delivery</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-[#ea580c] text-[#ea580c]">
                        <svg className="h-3.5 w-3.5 stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="leading-tight text-left">
                        <div className="text-xs font-bold text-[#00a859] sm:text-sm">Hurry!</div>
                        <div className="text-xs font-bold text-[#ea580c] sm:text-sm">Limited Stock Available</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Variants Selector */}
          <div>
            <div className="flex justify-end">
              <span className="text-xs font-semibold text-[#00a859] sm:text-sm">
                {selectedVariant
                  ? `${selectedVariant.variant_name}${selectedVariant.unit_name ? ` ${selectedVariant.unit_name}` : ""}`
                  : "1 ltr"}{" "}
                selected
              </span>
            </div>

          <div className="mt-2 flex flex-wrap items-center gap-3 sm:gap-4">
              {(product.variants && product.variants.length > 0
                ? product.variants
                : [
                    {
                      product_variant_id: 1,
                      variant_name: "1 Ltr",
                      discount_price: "321.00",
                      original_price: "399.00",
                    },
                    {
                      product_variant_id: 2,
                      variant_name: "2 Ltr",
                      discount_price: "629.00",
                      original_price: "799.00",
                    },
                    {
                      product_variant_id: 3,
                      variant_name: "5 Ltr",
                      discount_price: "1551.00",
                      original_price: "1995.00",
                    },
                  ]
              ).map((variant) => {
                const isSelected =
                  selectedVariant?.product_variant_id ===
                  variant.product_variant_id;
                const vDisc =
                  parseFloat(variant.original_price) >
                  parseFloat(variant.discount_price)
                    ? Math.round(
                        ((parseFloat(variant.original_price) -
                          parseFloat(variant.discount_price)) /
                          parseFloat(variant.original_price)) *
                          100,
                      )
                    : 0;

                // Check if THIS specific variant is in the cart
                const vKey = String(
                  variant.product_variant_id ?? variant.variant_id ?? variant.id ?? ''
                );
                const variantCartItem = variantCartMap[vKey];
                const variantInCart = !!variantCartItem;

                return (
                  <button
                    key={variant.product_variant_id}
                    type="button"
                    onClick={() => setSelectedVariant(variant)}
                    className={`relative min-w-[95px] rounded-2xl bg-white px-3.5 py-2.5 text-center transition-all sm:min-w-[105px] sm:py-3 ${
                      isSelected
                        ? "border-2 border-[#00a859] shadow-sm"
                        : variantInCart
                          ? "border-2 border-emerald-300 bg-emerald-50"
                          : "border-2 border-gray-700 hover:border-gray-900"
                    }`}
                  >
                    {vDisc > 0 && (
                      <span className="absolute -top-2.5 right-2 rounded-full bg-[#ea580c] px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                        {vDisc}%
                      </span>
                    )}
                    {/* In-cart badge: green count when this variant is already in cart */}
                    {variantInCart && !isSelected && (
                      <span className="absolute -left-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-black text-white shadow">
                        {variantCartItem.quantity}
                      </span>
                    )}
                    <div className="text-xs font-bold text-gray-900 sm:text-sm">
                      {variant.variant_name}
                      {variant.unit_name ? ` ${variant.unit_name}` : ""}
                    </div>
                    <div className="mt-0.5 text-sm font-extrabold text-gray-900 sm:text-base">
                      ₹{parseFloat(variant.discount_price).toFixed(0)}
                    </div>
                    {parseFloat(variant.original_price) >
                      parseFloat(variant.discount_price) && (
                      <div className="mt-0.5 text-[10px] text-gray-400 line-through sm:text-xs">
                        ₹ {parseFloat(variant.original_price).toFixed(2)}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Action Row: [Notify Me (if OOS)] OR [Add to Cart / Quantity Counter, Buy Now], [Subscribe] */}
          <div className="mt-6 flex flex-wrap items-center gap-3 sm:gap-4">
            {isOutOfStock ? (
              /* Out of stock: Show Out of Stock label and prominent Notify Me button */
              <>
                <div className="flex items-center gap-2 rounded-2xl bg-gray-100 px-6 py-2.5 text-base font-bold text-gray-500 sm:py-3.5 sm:text-lg">
                  <svg className="h-5 w-5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  <span>Out of Stock</span>
                </div>
                {selectedVariant && (
                  <NotifyMeButton
                    productId={productId}
                    variantId={
                      selectedVariant.product_variant_id ??
                      selectedVariant.variant_id ??
                      selectedVariant.id
                    }
                    pincode={pincode}
                    isAuthenticated={isAuthenticated}
                    openAuthModal={openAuthModal}
                  />
                )}
              </>
            ) : isInCart ? (
              /* Quantity Counter (shows after item is added to cart) */
              <div className="flex min-w-[125px] items-center justify-between gap-5 rounded-2xl bg-[#00a859] px-5 py-2.5 text-white shadow-sm sm:min-w-[135px] sm:py-3">
                <button
                  type="button"
                  onClick={handleDecrement}
                  disabled={cartLoading}
                  className="text-xl font-bold transition-opacity hover:opacity-80 disabled:opacity-40"
                  aria-label="Decrease quantity"
                >
                  -
                </button>
                <span className="text-base font-bold sm:text-lg">
                  {displayQuantity}
                </span>
                <button
                  type="button"
                  onClick={handleIncrement}
                  disabled={cartLoading}
                  className="text-xl font-bold transition-opacity hover:opacity-80 disabled:opacity-40"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
            ) : (
              /* Add to Cart Button (shows by default before item is added) */
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={!selectedVariant || cartLoading || addingToCart}
                className="flex items-center gap-2 rounded-2xl border-2 border-[#00a859] bg-white px-6 py-2.5 text-base font-bold text-[#00a859] shadow-sm transition-all hover:bg-[#00a859] hover:text-white disabled:cursor-not-allowed disabled:opacity-50 sm:py-3.5 sm:text-lg"
              >
                <FaShoppingCart className="h-4 w-4" />
                Add to Cart
              </button>
            )}

            {/* Buy Now Button (only when in stock) */}
            {!isOutOfStock && (
              <button
                type="button"
                onClick={handleBuyNow}
                disabled={!selectedVariant || cartLoading}
                className="flex min-w-[130px] items-center justify-center rounded-2xl bg-[#00a859] px-8 py-2.5 text-base font-bold text-white shadow-sm transition-all hover:bg-[#00924c] disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[150px] sm:py-3.5 sm:text-lg"
              >
                Buy Now
              </button>
            )}

            {/* Subscribe Button — visible only when product has subscription enabled */}
            {product.is_subscription_enabled && (
              <button
                type="button"
                onClick={() => {
                  if (!isAuthenticated) {
                    toast("Please login to subscribe", { icon: "🔒", id: "auth-required-toast" });
                    if (openAuthModal) openAuthModal();
                    return;
                  }
                  const variantId = selectedVariant?.product_variant_id || "";
                  navigate(
                    `/account/subscriptions?productId=${product.product_id ?? product.id}${variantId ? `&variantId=${variantId}` : ""}`
                  );
                }}
                className="flex min-w-[130px] items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#046a38] to-[#0d9e5c] px-6 py-2.5 text-base font-bold text-white shadow-md transition-all hover:shadow-lg hover:brightness-110 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[150px] sm:py-3.5 sm:text-lg"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Subscribe
              </button>
            )}

            {/* Show More / Show Less Button */}
            <button
              type="button"
              onClick={handleToggleAccordion}
              className="flex min-w-[110px] flex-col items-center justify-center rounded-2xl border-2 border-[#00a859] bg-white px-5 py-1.5 shadow-sm transition-all hover:bg-emerald-50/40 sm:min-w-[125px] sm:py-2"
            >
              <span className="text-xs font-bold text-[#00a859] sm:text-sm">
                {accordionOpen ? "Show Less" : "Show More"}
              </span>
              <span className="mt-0.5 text-[9px] leading-none text-[#00a859]">
                {accordionOpen ? "▲" : "▼"}
              </span>
            </button>
          </div>
        </div>
      </div>

      <ProductDetailsAccordion
        product={product}
        isOpen={accordionOpen}
        onToggle={() => setAccordionOpen((prev) => !prev)}
      />
    </>
  );
};

export default ProductDetailView;
